import type React from "react";
import { useState } from "react";
import { Box, Grid, MenuItem, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../../theme";
import CustomTextField from "../../../../components/ui/Textfield";
import CustomSelect from "../../../../components/ui/Select";
import CustomTable from "../../../../components/ui/Table";
import Chip from "../../../../components/ui/Chip";
import { PERSONALITY_TYPES, STATUS_STYLES } from "../../constants";
import type { ApplicationSubmission, JobPosition } from "../../types";

interface ApplicantsTableProps {
  submissions: ApplicationSubmission[];
  jobs: JobPosition[];
  onSelectSubmission: (submission: ApplicationSubmission) => void;
}

const ApplicantsTable: React.FC<ApplicantsTableProps> = ({
  submissions,
  jobs,
  onSelectSubmission,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [personalityFilter, setPersonalityFilter] = useState("");
  const [scoreFilter, setScoreFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const updateFilter = <T,>(setter: (value: T) => void) => (value: T) => {
    setPage(0);
    setter(value);
  };

  const filteredSubmissions = submissions.filter((sub) => {
    const matchesSearch =
      sub.candidateInfo.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.candidateInfo.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !roleFilter || sub.jobId === roleFilter;
    const matchesPersonality = !personalityFilter || sub.personalityResult?.code === personalityFilter;

    let matchesScore = true;
    if (scoreFilter === "high") matchesScore = sub.overallScore >= 80;
    else if (scoreFilter === "medium") matchesScore = sub.overallScore >= 60 && sub.overallScore < 80;
    else if (scoreFilter === "low") matchesScore = sub.overallScore < 60;

    return matchesSearch && matchesRole && matchesPersonality && matchesScore;
  });

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: colors.primary[700], display: "block", mb: 0.75 }}>
            Search
          </Typography>
          <CustomTextField
            size="small"
            placeholder="Name or email..."
            value={searchQuery}
            onChange={(e) => updateFilter(setSearchQuery)(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: colors.primary[700], display: "block", mb: 0.75 }}>
            Role
          </Typography>
          <CustomSelect
            label=""
            size="small"
            displayEmpty
            value={roleFilter}
            onChange={(e) => updateFilter(setRoleFilter)(e.target.value as string)}
            renderValue={(value) =>
              !value ? "All roles" : jobs.find((j) => j.id === value)?.title ?? "All roles"
            }
          >
            <MenuItem value="">All roles</MenuItem>
            {jobs.map((j) => (
              <MenuItem key={j.id} value={j.id}>
                {j.title}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: colors.primary[700], display: "block", mb: 0.75 }}>
            Personality Type
          </Typography>
          <CustomSelect
            label=""
            size="small"
            displayEmpty
            value={personalityFilter}
            onChange={(e) => updateFilter(setPersonalityFilter)(e.target.value as string)}
            renderValue={(value) => (!value ? "All types" : String(value))}
          >
            <MenuItem value="">All types</MenuItem>
            {PERSONALITY_TYPES.map((p) => (
              <MenuItem key={p.code} value={p.code}>
                {p.code}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: colors.primary[700], display: "block", mb: 0.75 }}>
            Assessment Score
          </Typography>
          <CustomSelect
            label=""
            size="small"
            displayEmpty
            value={scoreFilter}
            onChange={(e) => updateFilter(setScoreFilter)(e.target.value as string)}
            renderValue={(value) => {
              if (value === "high") return "High Score (80%+)";
              if (value === "medium") return "Medium Score (60-79%)";
              if (value === "low") return "Low Score (< 60%)";
              return "All scores";
            }}
          >
            <MenuItem value="">All scores</MenuItem>
            <MenuItem value="high">High Score (80%+)</MenuItem>
            <MenuItem value="medium">Medium Score (60-79%)</MenuItem>
            <MenuItem value="low">Low Score (&lt; 60%)</MenuItem>
          </CustomSelect>
        </Grid>
      </Grid>

      <CustomTable<ApplicationSubmission>
        columns={[
          {
            id: "candidateInfo",
            label: "Candidate",
            render: (sub) => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {sub.candidateInfo.fullName}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  {sub.candidateInfo.email}
                </Typography>
              </Box>
            ),
          },
          {
            id: "jobTitle",
            label: "Role",
            render: (sub) => (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {sub.jobTitle}
              </Typography>
            ),
          },
          {
            id: "personalityResult",
            label: "Personality",
            render: (sub) => (
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {sub.personalityResult?.code ?? "—"}
              </Typography>
            ),
          },
          {
            id: "overallScore",
            label: "Score",
            render: (sub) => (
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {sub.overallScore}/100
              </Typography>
            ),
          },
          {
            id: "status",
            label: "Status",
            render: (sub) => {
              const statusStyle = STATUS_STYLES[sub.status];
              return <Chip status={statusStyle.status} label={statusStyle.label} sx={{ fontSize: "0.75rem" }} />;
            },
          },
        ]}
        data={filteredSubmissions.slice(page * pageSize, (page + 1) * pageSize)}
        emptyMessage="No applicants match your filter criteria."
        onRowClick={onSelectSubmission}
        pagination={{
          currentPage: page,
          pageSize,
          totalCount: filteredSubmissions.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
        }}
      />
    </Box>
  );
};

export default ApplicantsTable;
